import { defineStore } from 'pinia'
import {
  type UserResponse,
  apiFastLogin,
  apiLogin,
  apiLoginWithEmail,
  apiRefreshToken,
  apiTotalPoint,
} from '@/api/user'
import { router } from '@/router'
import { emitter } from '@/common/mitt'
import { useWebSocket } from '@/common/useWebSocket'
import { useAssetsStore } from '@/store/modules/useAssetsStore'
import { useAiCreateStore } from '@/store/modules/useAiCreateStore'
import { useGenerationHistoryStore } from '@/store/modules/useGenerationHistoryStore'

export const useUserStore = defineStore('user', {
  state: () => ({
    isReady: false,
    user_id: localStorage.getItem('user-id') || '',
    avatar: localStorage.getItem('user-avatar') || '',
    name: localStorage.getItem('user-name') || '',
    nickname: localStorage.getItem('user-nickname') || '',
    email: localStorage.getItem('user-email') || '',
    access_token: localStorage.getItem('access-token') || '',
    refresh_token: localStorage.getItem('refresh-token') || '',
    expired_at: 0,
    points: localStorage.getItem('user-points') || 0,
  }),
  getters: {
    isLoggedIn: (state) => {
      return !!state.access_token
    },
    getAvatar: (state) => {
      return state.avatar
    },
    getToken: (state) => {
      return state.access_token
    },
  },
  actions: {
    async fastLogin(data: { credential: string }) {
      return new Promise<UserResponse>((resolve, reject) => {
        apiFastLogin(data)
          .then((data) => {
            this.parseLoginRes(data)
            resolve(data)
          })
          .catch((err) => {
            reject(err)
          })
      })
    },
    async login(data: { code: string; state: string; redirect_uri: string }) {
      return new Promise<UserResponse>((resolve, reject) => {
        apiLogin(data)
          .then((data) => {
            this.parseLoginRes(data)
            resolve(data)
          })
          .catch((err) => {
            reject(err)
          })
      })
    },
    async loginWithEmail(data: {
      email: string
      code: string
      client_code: string
    }) {
      return new Promise<UserResponse>((resolve, reject) => {
        apiLoginWithEmail(data)
          .then((data) => {
            this.parseLoginRes(data)
            resolve(data)
          })
          .catch((err) => {
            reject(err)
          })
      })
    },
    parseLoginRes(data) {
      this.user_id = data.user_id
      this.avatar = data.avatar
      this.name = data.name
      this.nickname = data.nickname
      this.email = data.email
      localStorage.setItem('user-id', data.user_id)
      localStorage.setItem('user-avatar', data.avatar)
      localStorage.setItem('user-name', data.name)
      localStorage.setItem('user-nickname', data.nickname)
      localStorage.setItem('user-email', data.email)
      this.updateToken(data.token)
      this.refreshPoints()
      emitter.emit('loginSuccess')
    },
    updateToken(data) {
      this.access_token = data.access_token
      this.refresh_token = data.refresh_token
      this.expired_at = data.expired_at
      localStorage.setItem('access-token', data.access_token)
      localStorage.setItem('refresh-token', data.refresh_token)
      localStorage.setItem('expired-at', data.expired_at)
    },
    async refreshToken() {
      if (!this.refresh_token) {
        this.isReady = true
        return
      }
      return new Promise<UserResponse>((resolve, reject) => {
        apiRefreshToken({
          refresh_token: this.refresh_token,
        })
          .then((data) => {
            this.updateToken(data)
            this.refreshPoints()
            resolve(data)
          })
          .catch((err) => {
            reject(err)
            this.logout()
          })
          .finally(() => {
            this.isReady = true
          })
      })
    },
    async refreshPoints() {
      apiTotalPoint()
        .then((data) => {
          this.points = data.total_points
          localStorage.setItem('user-points', data.total_points)
        })
        .catch((err) => {})
    },
    logout() {
      this.user_id = ''
      this.avatar = ''
      this.username = ''
      this.email = ''
      this.points = 0
      this.access_token = ''
      this.refresh_token = ''
      this.expired_at = 0
      localStorage.removeItem('user-id')
      localStorage.removeItem('user-avatar')
      localStorage.removeItem('user-name')
      localStorage.removeItem('user-nickname')
      localStorage.removeItem('user-email')
      localStorage.removeItem('access-token')
      localStorage.removeItem('refresh-token')
      localStorage.removeItem('expired-at')
      localStorage.removeItem('user-points')
      // router.push("/login");
      emitter.emit('logoutSuccess')
      const assetsStore = useAssetsStore(),
        generatorStore = useAiCreateStore(),
        generationHistoryStore = useGenerationHistoryStore()
      assetsStore.clearData()
      generatorStore.clearExhibition()
      generatorStore.clearLeftMenu()
      generationHistoryStore.clearData()
    },
  },
})
